import type { ProgetAppReleaseOutput } from '@mxa-argos/core/graphql/graphql';
import {
  CONNECTION_RETRY_DELAY_MS,
  CONNECTION_STAGGER_MS,
  DEFAULT_PACKAGES,
  MAX_CONCURRENT_INSTALLS,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type {
  ForjaBatchState,
  ForjaDownloadEvent,
} from '@mxa-argos/modules/forja/interfaces/forja-batch-state.interface';
import type { ForjaBatchDeviceSummary } from '@mxa-argos/modules/forja/interfaces/forja-batch-summary.interface';
import type { ForjaDeviceState } from '@mxa-argos/modules/forja/interfaces/forja-device-state.interface';
import type { ForjaDeviceStatus } from '@mxa-argos/modules/forja/interfaces/forja-device-status.interface';
import type { ForjaProgressEvent } from '@mxa-argos/modules/forja/interfaces/forja-progress-event.interface';
import type { ForjaStepResult } from '@mxa-argos/modules/forja/interfaces/forja-step-result.interface';
import type { ForjaStepState } from '@mxa-argos/modules/forja/interfaces/forja-step-state.interface';
import { ForjaDeviceSession } from '@mxa-argos/modules/forja/lib/engine/forja-device-session';
import { ForjaEngineEvents } from '@mxa-argos/modules/forja/lib/engine/forja-engine-events';
import {
  ForjaError,
  toForjaErrorCode,
  toForjaErrorDetail,
} from '@mxa-argos/modules/forja/lib/forja-error';
import { playStartSound } from '@mxa-argos/modules/forja/lib/play-start-sound';
import { downloadApks } from '@mxa-argos/modules/forja/lib/provisioning/download-apks';
import { provisionBatch } from '@mxa-argos/modules/forja/lib/provisioning/provision-batch';
import { Semaphore } from '@mxa-argos/modules/forja/lib/semaphore';
import { sleep } from '@mxa-argos/modules/forja/lib/sleep';
import { connectDevice } from '@mxa-argos/modules/forja/lib/transport/connect-device';
import {
  findWebUSBManager,
  isWebUSBSupported,
} from '@mxa-argos/modules/forja/lib/transport/webusb-manager';
import { forjaLogStore } from '@mxa-argos/modules/forja/services/forja-log.store';
import { findProvisioningAppReleases } from '@mxa-argos/modules/forja/services/forja-ops.service';
import type { AdbDaemonWebUsbDevice } from '@yume-chan/adb-daemon-webusb';

const EMPTY_STATE: ForjaBatchState = { devices: [], downloads: [], lastBatch: null };

const RESTARTABLE_STATUSES = new Set<ForjaDeviceStatus>(['IDLE', 'DONE', 'FAILED']);

interface ClaimedDevice {
  serial: string;
  imei: string;
  session: Promise<ForjaDeviceSession>;
}

// Outside React so that navigating away does not interrupt a batch or drop the USB connections.
class ForjaBatchStore {
  private state: ForjaBatchState = EMPTY_STATE;
  private readonly listeners = new Set<() => void>();
  private readonly sessions = new Map<string, Promise<ForjaDeviceSession>>();
  private readonly usbDevices = new Map<string, AdbDaemonWebUsbDevice>();
  private readonly retryTimers = new Map<string, number>();
  private readonly events = new ForjaEngineEvents();
  // Shared across batches: concurrent APK uploads starve the USB bandwidth and look stalled.
  private readonly installSlots = new Semaphore(MAX_CONCURRENT_INSTALLS);
  private pendingRegistrations: Promise<void> = Promise.resolve();
  private isObserving = false;

  constructor() {
    this.events.onProgress((event) => this.applyProgress(event));
    this.events.onProgress((event) => forjaLogStore.applyProgress(event));
    this.events.onShellLog((event) => forjaLogStore.appendShell(event));
    window.addEventListener('beforeunload', (event) => {
      if (this.hasRunningDevices) {
        event.preventDefault();
      }
    });
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ForjaBatchState => this.state;

  observeDevices = async (): Promise<void> => {
    if (this.isObserving || !isWebUSBSupported()) {
      return;
    }

    this.isObserving = true;

    const observer = await findWebUSBManager().trackDevices();

    observer.onDeviceAdd((added) => {
      for (const device of added) {
        this.enqueueRegistration(device);
      }
    });

    observer.onDeviceRemove((removed) => {
      for (const device of removed) {
        this.forgetDevice(device.serial);
      }
    });

    for (const device of observer.current) {
      this.enqueueRegistration(device);
    }
  };

  retryDevice = async (serial: string): Promise<void> => {
    const device = this.usbDevices.get(serial);

    if (!device || this.isRunning(serial)) {
      return;
    }

    this.cancelRetry(serial);
    forjaLogStore.appendLifecycle(serial, 'retrying connection');
    await this.releaseDevice(serial);
    this.patchDevice(serial, {
      status: 'CONNECTING',
      errorCode: null,
      wait: { reason: 'AUTHORIZATION', untilEpochMillis: null },
    });

    await this.registerDevice(device);
  };

  requestDevice = async (): Promise<void> => {
    const device = await findWebUSBManager().requestDevice();

    if (device) {
      await this.registerDevice(device);
    }
  };

  powerOffDevices = async (serials: string[]): Promise<void> => {
    const sessions = await this.collectConnectedSessions(serials);

    await Promise.all(sessions.map((session) => session.powerOff()));
  };

  rebootDevice = async (serial: string): Promise<void> => {
    const [session] = await this.collectConnectedSessions([serial]);

    if (!session) {
      return;
    }

    await session.reboot();
  };

  start = async (serials: string[]): Promise<void> => {
    const selected = new Set(serials);
    const claimed: ClaimedDevice[] = [];
    const startedAtEpochMillis = Date.now();

    for (const device of this.state.devices) {
      const session = this.sessions.get(device.serial);

      if (
        !selected.has(device.serial) ||
        !RESTARTABLE_STATUSES.has(device.status) ||
        !device.imei ||
        !session
      ) {
        continue;
      }

      // Claimed before the first await: a second click while the APKs download must not start the
      // same device twice.
      this.patchDevice(device.serial, {
        status: 'RUNNING',
        phase: null,
        plan: [],
        steps: [],
        errorCode: null,
        wait: null,
        install: null,
        startedAtEpochMillis,
        finishedAtEpochMillis: null,
      });
      claimed.push({ serial: device.serial, imei: device.imei, session });
    }

    if (claimed.length === 0) {
      return;
    }

    playStartSound();

    const groups = new Map<
      string,
      { releases: ProgetAppReleaseOutput[]; devices: ClaimedDevice[] }
    >();

    try {
      const batch = await findProvisioningAppReleases(
        claimed.map((device) => device.imei),
        DEFAULT_PACKAGES,
      );

      for (const failure of batch.failed) {
        this.failClaimed(
          claimed.filter((device) => device.imei === failure.imei),
          new ForjaError('APP_RELEASES_UNRESOLVED', failure.message),
        );
      }

      for (const { imei, releases } of batch.resolved) {
        const key = releases.map((release) => release.progetApplicationUUID).join('|');
        const group = groups.get(key) ?? { releases, devices: [] };
        group.devices.push(...claimed.filter((device) => device.imei === imei));
        groups.set(key, group);
      }
    } catch (error) {
      this.failClaimed(claimed, error);
    }

    await Promise.all(
      [...groups.values()].map(({ releases, devices }) => this.provisionGroup(releases, devices)),
    );

    const devices: ForjaBatchDeviceSummary[] = [];

    for (const device of this.state.devices) {
      if (!selected.has(device.serial) || device.finishedAtEpochMillis === null) {
        continue;
      }

      devices.push({
        serial: device.serial,
        imei: device.imei,
        durationMs: device.finishedAtEpochMillis - startedAtEpochMillis,
        wasSuccessful: device.status === 'DONE',
      });
    }

    this.setState({
      ...this.state,
      lastBatch: { startedAtEpochMillis, finishedAtEpochMillis: Date.now(), devices },
    });
  };

  private async provisionGroup(
    releases: ProgetAppReleaseOutput[],
    claimed: ClaimedDevice[],
  ): Promise<void> {
    try {
      const apks = await downloadApks(releases, this.events);
      const devices = await Promise.all(claimed.map(({ session }) => session));
      await provisionBatch(devices, apks, this.installSlots);
    } catch (error) {
      this.failClaimed(claimed, error);
    }
  }

  private failClaimed(claimed: ClaimedDevice[], error: unknown): void {
    const errorCode = toForjaErrorCode(error);
    const errorDetail = toForjaErrorDetail(error);

    for (const { serial } of claimed) {
      this.events.emit({ type: 'finished', serial, wasSuccessful: false, errorCode, errorDetail });
    }
  }

  private get hasRunningDevices(): boolean {
    return this.state.devices.some((device) => device.status === 'RUNNING');
  }

  private isRunning(serial: string): boolean {
    return this.state.devices.some(
      (device) => device.serial === serial && device.status === 'RUNNING',
    );
  }

  private async collectConnectedSessions(serials: string[]): Promise<ForjaDeviceSession[]> {
    const selected = new Set(serials);
    const sessions: ForjaDeviceSession[] = [];

    for (const device of this.state.devices) {
      const session = this.sessions.get(device.serial);

      if (!selected.has(device.serial) || device.status === 'RUNNING' || !session) {
        continue;
      }

      sessions.push(await session);
    }

    return sessions;
  }

  // Prompts are answered one device at a time, so registering the whole bench at once only buries
  // the operator. The grace period bounds the queue without cancelling anything: a device whose
  // prompt is still open keeps authenticating in the background while the next one starts.
  private enqueueRegistration(device: AdbDaemonWebUsbDevice): void {
    this.pendingRegistrations = this.pendingRegistrations.then(() =>
      Promise.race([this.registerDevice(device), sleep(CONNECTION_STAGGER_MS)]),
    );
  }

  private registerDevice = async (device: AdbDaemonWebUsbDevice): Promise<void> => {
    this.usbDevices.set(device.serial, device);

    if (!this.state.devices.some((known) => known.serial === device.serial)) {
      this.setState({
        ...this.state,
        devices: [...this.state.devices, toInitialState(device)],
      });
    }

    if (this.sessions.has(device.serial)) {
      return;
    }

    forjaLogStore.appendLifecycle(device.serial, 'usb device registered, connecting');

    const session = connectDevice(device).then(
      (adb) => new ForjaDeviceSession(adb, device.serial, this.events),
    );
    this.sessions.set(device.serial, session);

    try {
      const imei = await session.then((forjaDevice) => forjaDevice.readIMEI());
      forjaLogStore.appendLifecycle(device.serial, 'connection established');
      this.patchDevice(device.serial, { imei, status: 'IDLE', wait: null });
    } catch (error) {
      forjaLogStore.appendLifecycle(device.serial, 'connection failed', String(error));
      await this.releaseDevice(device.serial);
      this.patchDevice(device.serial, {
        status: 'FAILED',
        wait: {
          reason: 'CONNECTION_RETRY',
          untilEpochMillis: Date.now() + CONNECTION_RETRY_DELAY_MS,
        },
        errorCode: toForjaErrorCode(error),
      });
      this.scheduleRetry(device.serial);
    }
  };

  private scheduleRetry(serial: string): void {
    this.cancelRetry(serial);
    this.retryTimers.set(
      serial,
      window.setTimeout(() => {
        this.retryTimers.delete(serial);
        void this.retryDevice(serial);
      }, CONNECTION_RETRY_DELAY_MS),
    );
  }

  private cancelRetry(serial: string): void {
    const timer = this.retryTimers.get(serial);

    if (timer === undefined) {
      return;
    }

    window.clearTimeout(timer);
    this.retryTimers.delete(serial);
  }

  // A half-finished handshake leaves the USB interface claimed, and every later attempt then fails
  // with a claim error that no amount of retrying fixes.
  private async releaseDevice(serial: string): Promise<void> {
    this.sessions.delete(serial);
    await this.usbDevices
      .get(serial)
      ?.raw.close()
      .catch(() => undefined);
  }

  private forgetDevice(serial: string): void {
    forjaLogStore.appendLifecycle(serial, 'usb device removed');
    this.cancelRetry(serial);
    this.sessions.delete(serial);
    this.usbDevices.delete(serial);
    this.setState({
      ...this.state,
      devices: this.state.devices.filter((device) => device.serial !== serial),
    });
  }

  private applyProgress(event: ForjaProgressEvent): void {
    if (event.type === 'download') {
      this.setState({ ...this.state, downloads: replaceDownload(this.state.downloads, event) });
      return;
    }

    this.setState({
      ...this.state,
      devices: this.state.devices.map((device) => applyEvent(device, event)),
    });
  }

  private patchDevice(serial: string, patch: Partial<ForjaDeviceState>): void {
    this.setState({
      ...this.state,
      devices: this.state.devices.map((device) =>
        device.serial === serial ? { ...device, ...patch } : device,
      ),
    });
  }

  private setState(next: ForjaBatchState): void {
    this.state = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function replaceDownload(
  downloads: ForjaDownloadEvent[],
  event: ForjaDownloadEvent,
): ForjaDownloadEvent[] {
  const index = downloads.findIndex(
    (download) => download.progetApplicationUUID === event.progetApplicationUUID,
  );

  if (index === -1) {
    return [...downloads, event];
  }

  return downloads.map((download, position) => (position === index ? event : download));
}

function toInitialState(device: AdbDaemonWebUsbDevice): ForjaDeviceState {
  return {
    serial: device.serial,
    name: device.name,
    imei: null,
    status: 'CONNECTING',
    phase: null,
    plan: [],
    steps: [],
    errorCode: null,
    wait: { reason: 'AUTHORIZATION', untilEpochMillis: null },
    install: null,
    connectedAtEpochMillis: Date.now(),
    startedAtEpochMillis: null,
    finishedAtEpochMillis: null,
  };
}

function applyEvent(
  device: ForjaDeviceState,
  event: Exclude<ForjaProgressEvent, { type: 'download' }>,
): ForjaDeviceState {
  if (device.serial !== event.serial) {
    return device;
  }

  switch (event.type) {
    case 'plan':
      return { ...device, plan: event.steps, steps: [], errorCode: null };
    case 'wait-start':
      return {
        ...device,
        wait: { reason: event.reason, untilEpochMillis: event.untilEpochMillis },
      };
    case 'wait-end':
      return { ...device, wait: null };
    case 'phase-start':
      return { ...device, status: 'RUNNING', phase: event.phase };
    case 'step-start':
      return {
        ...device,
        status: 'RUNNING',
        steps: [
          ...device.steps,
          {
            name: event.name,
            status: 'RUNNING',
            startedAtEpochMillis: Date.now(),
            durationMs: null,
            attempt: 1,
          },
        ],
      };
    case 'step-retry':
      return {
        ...device,
        steps: device.steps.map((step) =>
          step.name === event.name && step.status === 'RUNNING'
            ? { ...step, attempt: event.attempt }
            : step,
        ),
      };
    case 'step-result':
      return {
        ...device,
        install: null,
        steps: replaceStep(device, event.result.name, event.result),
      };
    case 'install-progress':
      return {
        ...device,
        install: {
          packageName: event.packageName,
          state: event.state,
          transferredBytes: event.transferredBytes,
          totalBytes: event.totalBytes,
          bytesPerSecond: event.bytesPerSecond,
        },
      };
    case 'imei-read':
      return { ...device, imei: event.imei };
    case 'finished':
      return {
        ...device,
        status: event.wasSuccessful ? 'DONE' : 'FAILED',
        phase: null,
        wait: null,
        errorCode: event.errorCode ?? device.errorCode,
        finishedAtEpochMillis: Date.now(),
      };
  }
}

function replaceStep(
  device: ForjaDeviceState,
  name: string,
  result: ForjaStepResult,
): ForjaStepState[] {
  const index = device.steps.findLastIndex((step) => step.name === name);

  if (index === -1) {
    return [
      ...device.steps,
      { ...result, startedAtEpochMillis: null, durationMs: null, attempt: 1 },
    ];
  }

  return device.steps.map((step, position) => {
    if (position !== index) {
      return step;
    }

    const startedAtEpochMillis = step.startedAtEpochMillis;

    return {
      ...result,
      startedAtEpochMillis,
      durationMs: startedAtEpochMillis === null ? null : Date.now() - startedAtEpochMillis,
      attempt: step.attempt,
    };
  });
}

export const forjaBatchStore = new ForjaBatchStore();
